import { tooltip } from './_base';

/**
 * Health is the only number in the header that ends the run, and it used to be set in the same
 * weight, the same size and the same grey as the coin count beside it. A player could walk from
 * five to zero without the page ever acknowledging it, and the death that follows reads as a bug
 * rather than as a consequence.
 *
 * So health carries a *band* as well as a value, and the interface reads the band. Nothing new is
 * drawn while it is CLEAR — this is still a game whose header is a row of plain numbers — and each
 * step down buys a louder page: FAILING puts the weight into the readout, DYING dims what is
 * around it and closes the light in from the edges of the screen (Page.style.js).
 *
 * ── Why the bands are a share of `peak` and not of `max` ────────────────────────────────────
 *
 * `max` is 100 and the run opens at 5. A fraction of the ceiling would call the entire walk a
 * death rattle — the walk is *designed* to end at exactly zero — and would then mean nothing at
 * all by the time the player is buying health back in tens. Neither is information.
 *
 * `peak` is the most health the traveler has ever carried: the only scale the game has actually
 * shown him, and so the only one he can be judged against. It rises with the run and never falls,
 * tracked in Resources.delta.
 *
 * The absolute floors underneath it keep the early game honest, where every fraction of 5 rounds
 * away to nothing.
 */
const HealthBands = { CLEAR: 'clear', FAILING: 'failing', DYING: 'dying' };

// At peak 5 these read as "two left" and "one left". At peak 60 they are 24 and 9 — and the
// road's ordinary bite is 5 to 15 health, so DYING means the next ordinary thing to happen can
// finish it. That is the whole definition, and it is the one the player has to be able to feel.
const FAILING_SHARE = 0.4;
const DYING_SHARE = 0.15;
const FAILING_FLOOR = 2;
const DYING_FLOOR = 1;

const failingAt = peak => Math.max(FAILING_FLOOR, peak * FAILING_SHARE);
const dyingAt = peak => Math.max(DYING_FLOOR, peak * DYING_SHARE);

/**
 * The band for a health attrib. Takes the resource itself so callers can hand over
 * `party.resources.attribs.health` without unpacking it.
 *
 * Zero bands as DYING rather than as anything of its own: the dead have a modal, and no part of
 * the interface should be styling a corpse.
 */
const healthBand = ({ value = 0, peak = 0 } = {}) => {
  if (value <= dyingAt(peak)) return HealthBands.DYING;
  if (value <= failingAt(peak)) return HealthBands.FAILING;
  return HealthBands.CLEAR;
};

// The log is the only narrator, so a crossing is said once, in the traveler's voice: what the
// body did, and nothing about what it means or how it feels.
const CROSSING = {
  [HealthBands.FAILING]: 'hands shake. i put them under my arms and they shake anyway',
  [HealthBands.DYING]: 'the light goes narrow. i am counting breaths now to keep them coming'
};

const RANK = { [HealthBands.CLEAR]: 0, [HealthBands.FAILING]: 1, [HealthBands.DYING]: 2 };

/**
 * The line to log for a change in health, or null.
 *
 * Only on the way *down*, and only into a band the traveler was not already in — a man on this
 * road does not remark on feeling better, and the page clearing says it anyway. Never at zero:
 * death opens a modal of its own and does not need to be introduced.
 */
const healthCrossing = (from, to, value) => {
  if (value <= 0) return null;
  if (RANK[to] <= RANK[from]) return null;
  return CROSSING[to] ?? null;
};

const Health = () => ({
  tooltip: () => tooltip('health'),
  max: 100,
  min: 0,
  // The high-water mark the bands are measured against. Resources.delta raises it; Resources.init
  // seeds it from the starting value and resets it with the run.
  peak: 0,
  trackPeak: true
});

export { HealthBands, healthBand, healthCrossing };
export default Health;
